<template>
  <section v-if="item" :class="['ecom summary', { 'projects__label--disabled': !item }]">
    <h2 class="ecom summary__header">
      Order Summary
      <img :src="cartIcon" class="ecom summary__cart-icon" />
    </h2>
    <section class="summary__address-wrapper">
      <h3 class="summary__address-header">
        <img class="summary__address-pin" :src="pinIcon" />
        Delivering to:
      </h3>
      <p class="summary__address" v-if="item">
        <span class="summary__address-text" v-if="formattedServiceAddress">{{formattedServiceAddress}}</span>
        <button
          v-if="!hideEdit"
          @click="openLocationModal"
          class="summary__address-edit"
          type="button"
        >
          edit
        </button>
      </p>
      <template v-if="deliveryDate">
        <h3 class="summary__address-header mt-1">
          <img class="summary__address-pin" :src="calendarIcon" />
          Delivery Date:
        </h3>
        <p class="summary__address">
          <span class="summary__address-text">{{deliveryDate}}</span>
        </p>
      </template>
    </section>
    <section class="summary__info-wrapper">
      <h3 class="summary__info-header">Container</h3>
      <p class="summary__info-product" v-if="item">{{item.size}} Yard Dumpster</p>
      <p class="summary__info-price-wrapper" v-if="item">
        <span>Flat Rate Price</span>
        <span>${{item.price}}</span>
      </p>
    </section>
    <section class="summary__info-wrapper">
      <h3 class="summary__info-header">Service</h3>
      <ul class="summary__service-list" v-if="item">
        <li class="summary__service-item">
          <span class="summary__service-item-name">{{item.tonnage}} Ton(s) of Debris</span>
          <span class="summary__service-item-included">Included</span>
        </li>
        <li class="summary__service-item">
          <span class="summary__service-item-name">{{getNumericValue(item.rentalPeriod)}} Day Rental</span>
          <span class="summary__service-item-included">Included</span>
        </li>
        <li v-for="service in services" :key="service.name" class="summary__service-item">
          <span class="summary__service-item-name" v-html="service.name"></span>
          <span class="summary__service-item-included">{{service.value}}</span>
        </li>
        <li v-if="discountCodeValid" class="summary__service-item summary__service--discount">
          <span class="summary__service-item-name">Promotional Discount</span>
          <span class="summary__service-item-included">-${{discountAmount}}.00</span>
        </li>
      </ul>
    </section>
    <p class="summary__final-cost">
      <span class="summary__final-cost-text">Total Cost:</span>
      <span class="summary__final-cost-price">
        <template v-if="item">
          ${{totalAmount}}
        </template>
        <template v-else>
          $--
        </template>
      </span>
    </p>
    <div v-if="item && $route.path !== '/cart/confirmation'" class="summary__lock-rate" @click="lockInRate(item)">
      <img class="summary__lock-rate-icon" :src="saveIcon">
      <span class="summary__lock-rate-link">Lock in Your Rate for Later</span>
    </div>
    <!-- Edit Location Modal -->
    <modal
      :isOpen=isLocationModalOpen
      @close-modal="closeModal"
      className="modal edit-location-modal"
      showClose
    >
      <template v-slot:content>
        <img class="theme-logo" :src="themeLogo" />
        <div class="edit-location-modal__heading">Changing Your Service Address?</div>
        <div class="edit-location-modal__desc mt-1"><p><strong>Please note:</strong> Changing your service address may result in a change of pricing and availability. By submitting a new project location, you acknowledge that the details of your service order may change.</p></div>
        <form className="edit-location-modal__form mt-2" @submit.prevent>
          <div class="label__wrapper">
            <input
              :placeholder="formattedServiceAddress"
              class="input input__with-icon--right input__without-label"
              id="serviceZip"
              name="search"
              ref="addressChangeInput"
              type="text"
            />
            <img class="input__icon--right" :src="pinIcon">
          </div>
          <div class="button__wrapper">
            <button @click="closeModal" type="button" class="btn mt-2">
              Cancel
            </button>
            <button @click="changeAddress" type="button" class="btn btn--secondary mt-2">
              Submit New Address
              <img :src="iconArrowRight" />
            </button>
          </div>
        </form>
      </template>
    </modal>
  </section>
</template>

<!-- eslint-disable no-unused-vars -->
<script>
import { mapState, mapGetters, mapMutations } from 'vuex'
import { lightFormat } from 'date-fns'
import Modal from '@/components/Modal.vue'
import config from '@/config'
import {
  getNumericValue,
  setupAutocomplete
} from '@/utilities.js'
const { cdn } = config

export default {
  name: 'OrderSummary',
  components: {
    Modal
  },
  props: {
    hideEdit: {
      default: false
    }
  },
  data: () => {
    return {
      isLocationModalOpen: false,
      themeLogo: `${cdn}theme-logo.png`,
      cartIcon: `${cdn}icons-cart.svg`,
      pinIcon: `${cdn}icons-address-marker.svg`,
      saveIcon: `${cdn}icons-save-cart.svg`,
      iconArrowRight: `${cdn}icons-arrow-right.svg`,
      calendarIcon: `${cdn}icons-calendar.svg`,
      envelope: `${cdn}envelope.svg`
    }
  },
  methods: {
    getNumericValue,
    ...mapMutations('lockRate', [
      'lockInRate'
    ]),
    closeModal () {
      this.isLocationModalOpen = false
    },
    changeAddress () {
      // This simulates pressing Enter so the submit address button also triggers the autocomplete place_changed event
      this.$refs.addressChangeInput.focus()
      this.$refs.addressChangeInput.dispatchEvent(new KeyboardEvent('keydown', {
        key: 'Enter',
        code: 'Enter',
        keyCode: 13
      }))
    },
    openLocationModal () {
      this.isLocationModalOpen = true
      this.$nextTick(() => {
        setupAutocomplete(this.$refs.addressChangeInput, (address) => {
          if (address) {
            this.closeModal()
            this.$emit('address-edit') // Closes the summary slider
            this.$store.commit('cart/item', null)
            this.$store.commit('customer/hasAcceptedRentalOverages', false)
            this.$store.commit('customer/hasAcceptedTonnageOverages', false)
            this.$store.commit('customer/serviceAddress', address)
            this.$store.commit('dumpster/makoResponse', null)
            this.$store.commit('dumpster/setSelectedDumpster', null)
            if (this.$route.path === '/cart') {
              window.scrollTo({ top: 0, behavior: 'smooth' })
            } else {
              // Navigate back to step 1 and clear the UUID so the saved cart doesn't get reloaded and wipe out the edited address
              this.$router.push({ name: 'ChooseDumpster', query: { uuid: 'remove' } })
            }
          }
        })
      })
    }
  },
  computed: {
    ...mapState('cart', [
      'discountCodeValid',
      'discountAmount',
      'item'
    ]),
    ...mapState('customer', [
      'serviceAddress'
    ]),
    ...mapState('lockRate', [
      'lockRateEmail',
      'lockRateName'
    ]),
    ...mapGetters('cart', [
      'taxAmount',
      'totalAmount'
    ]),
    ...mapGetters('customer', [
      'formattedServiceAddress'
    ]),
    services () {
      const includedServices = [
        { name: 'Delivery &amp; Pickup', value: 'Included' },
        { name: 'Disposal Costs', value: 'Included' }
      ]
      if (this.taxAmount) {
        includedServices.push({ name: 'Sales Tax', value: `$${this.taxAmount}` })
      }
      return includedServices
    },
    deliveryDate () {
      const deliveryDate = this.$store.state.customer.deliveryDate
      return deliveryDate && lightFormat(deliveryDate, 'M/d/yyyy')
    },
    selectedDumpsterSize () {
      return this.$store.state.dumpster.selectedDumpsterSize ?? 10
    }
  }
}
</script>
